import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, CalendarDays, ExternalLink, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import JobStatusBadge from '../JobStatusBadge';
import useJobConfig from '../../hooks/useJobConfig';
import { UnitAttachments } from './UnitAttachments';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (d) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export default function UnitHistoryPanel({ customerId, unit }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { labelForType } = useJobConfig();

  useEffect(() => {
    let cancelled = false;
    setData(null); setError(false);
    const params = { asset_key: unit.id };
    axios.get(`${API}/customers/${customerId}/units/history`, { params })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [customerId, unit.id, unit.asset_revision]);

  const download = async (file) => {
    try {
      const { data: blob } = await axios.get(`${API}/customers/${customerId}/assets/${encodeURIComponent(unit.id)}/files/${file.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob), link = document.createElement('a'); link.href = url; link.download = file.name; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error('Asset file unavailable. Please try again.'); }
  };

  if (error) return <p className="text-sm text-red-600 px-4 py-3" role="alert" data-testid="asset-history-error">Failed to load history.</p>;
  if (!data) return <p className="text-sm text-slate-500 px-4 py-3" data-testid="asset-history-loading">Loading history…</p>;

  const timeline = [
    ...data.jobs.map((j) => ({ kind: 'job', date: j.start_date, ...j })),
    ...data.events.map((e) => ({ kind: 'event', ...e })),
  ].sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  const specs = Object.entries(unit.specifications || {}).filter(([k, v]) => v && k !== 'SIZE');
  const dspecs = Object.entries(unit.driver_specifications || {}).filter(([, v]) => v);
  const accessories = unit.accessories || [];
  const hasDetails = unit.size || unit.series || specs.length || unit.driver_type || accessories.length || unit.notes;
  const details = hasDetails ? (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm [overflow-wrap:anywhere] [&_dd]:min-w-0 [&>div]:min-w-0" data-testid="unit-history-details">
      <div>
        <p className="text-xs font-bold uppercase text-slate-500 mb-1">Specifications</p>
        <dl className="space-y-0.5 text-xs">
          {unit.size && <div className="flex gap-2"><dt className="text-slate-500 w-32 shrink-0">Size</dt><dd className="text-slate-800" data-testid={`unit-history-size-${encodeURIComponent(unit.id)}`}>{unit.size}</dd></div>}
          {unit.series && <div className="flex gap-2"><dt className="text-slate-500 w-32 shrink-0">Series / frame</dt><dd className="text-slate-800">{unit.series}</dd></div>}
          {specs.map(([k, v]) => <div key={k} className="flex gap-2"><dt className="text-slate-500 w-32 shrink-0 truncate">{k.toLowerCase()}</dt><dd className="text-slate-800">{v}</dd></div>)}
          {!unit.size && !unit.series && !specs.length && <dd className="text-slate-400">—</dd>}
        </dl>
      </div>
      <div>
        <p className="text-xs font-bold uppercase text-slate-500 mb-1">Driver{unit.driver_type ? ` · ${unit.driver_type.toLowerCase()}` : ''}</p>
        <dl className="space-y-0.5 text-xs">
          {dspecs.map(([k, v]) => <div key={k} className="flex gap-2"><dt className="text-slate-500 w-32 shrink-0 truncate">{k.toLowerCase()}</dt><dd className="text-slate-800">{v}</dd></div>)}
          {!unit.driver_type && <dd className="text-slate-400">—</dd>}
        </dl>
      </div>
      <div>
        <p className="text-xs font-bold uppercase text-slate-500 mb-1">Accessories ({accessories.length})</p>
        <ul className="space-y-0.5 text-xs">
          {accessories.map((a, i) => <li key={i} className="text-slate-800">{[a.type, a.manufacturer, a.model, a.size].filter(Boolean).join(' · ')}</li>)}
          {!accessories.length && <li className="text-slate-400">—</li>}
        </ul>
        {unit.notes && <p className="text-xs text-slate-600 mt-2 whitespace-pre-wrap">{unit.notes}</p>}
      </div>
    </div>
  ) : null;

  const files = <UnitAttachments ids={data.attachment_ids || unit.attachment_ids} prefix={`unit-history-files-${encodeURIComponent(unit.id)}`} />;
  const sourceNotes = !!data.source_notes?.length && <div className="mb-4 space-y-2" data-testid="asset-source-notes">{data.source_notes.map((note, i) => <div key={i} className="text-sm border-l-2 border-slate-200 pl-3" data-testid={`asset-source-note-${i}`}><p className="text-xs text-slate-500">{note.source}</p><p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{note.content}</p></div>)}</div>;
  if (!timeline.length) return <div className="px-4 py-3">{details}{files}{sourceNotes}<p className="text-sm text-slate-500" data-testid="unit-history-empty">No jobs or field events recorded for this asset yet.</p></div>;

  return (
    <div className="px-4 py-3" data-testid="unit-history-panel">
      {details}
      {files}
      {sourceNotes}
      <p className="text-xs font-bold uppercase text-slate-500 mb-2">
        Service history — {unit.unit_id || unit.serial_number} @ {unit.lsd || 'no LSD'} · {data.jobs.length} job{data.jobs.length === 1 ? '' : 's'}, {data.events.length} field event{data.events.length === 1 ? '' : 's'}
      </p>
      <ol className="relative border-l border-slate-200 ml-2 space-y-3">
        {timeline.map((t) => (
          <li key={`${t.kind}-${t.id}`} className="ml-4 relative" data-testid={`unit-history-${t.kind}-${t.id}`}>
            <span className={`absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white ${t.kind === 'job' ? 'bg-slate-900' : ''}`} style={t.kind === 'event' ? { backgroundColor: t.category_color || '#0ea5e9' } : undefined} />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="text-xs text-slate-500 w-28 shrink-0">{fmt(t.date)}{t.kind === 'job' && t.end_date && t.end_date !== t.date ? ` → ${fmt(t.end_date)}` : ''}</span>
              {t.kind === 'job' ? (
                <>
                  <Briefcase size={14} className="text-slate-400" />
                  <span className="font-semibold text-slate-900">Job #{t.job_number}</span>
                  <span className="text-slate-500">{labelForType(t.job_type) || t.job_type}</span>
                  <JobStatusBadge status={t.status} />
                  {t.assigned_tech_names?.length > 0 && <span className="text-xs text-slate-600">{t.assigned_tech_names.join(', ')}</span>}
                  <button type="button" onClick={() => navigate(`/jobs/${t.id}`)} className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline" data-testid={`unit-history-open-job-${t.id}`}>
                    Open <ExternalLink size={11} />
                  </button>
                </>
              ) : (
                <>
                  <CalendarDays size={14} className="text-slate-400" />
                  <span className="font-semibold text-slate-900">{t.title}</span>
                  {t.category && <span className="px-1.5 py-0.5 rounded text-[11px] border" style={{ borderColor: t.category_color, color: t.category_color }}>{t.category}</span>}
                  {t.calendar && <span className="text-xs text-slate-500">{t.calendar}</span>}
                  {t.attendee_names?.length > 0 && <span className="text-xs text-slate-600">{t.attendee_names.join(', ')}</span>}
                  {t.job_id && (
                    <button type="button" onClick={() => navigate(`/jobs/${t.job_id}`)} className="inline-flex items-center gap-1 text-xs text-sky-700 hover:underline">
                      Job #{t.job_number} <ExternalLink size={11} />
                    </button>
                  )}
                </>
              )}
            </div>
            {t.kind === 'job' && t.description && <p className="text-xs text-slate-500 mt-0.5 sm:ml-[7.75rem] [overflow-wrap:anywhere] max-w-xl">{t.description}</p>}
            {t.notes?.map((note) => <div key={note.id} className="text-sm mt-2 sm:ml-[7.75rem] [overflow-wrap:anywhere]" data-testid={`asset-history-note-${note.id}`}><p className="whitespace-pre-wrap">{note.content}</p><p className="text-xs text-slate-500">{note.author} · {note.date}</p></div>)}
            {t.files?.length > 0 && (
              <ul className="flex flex-wrap gap-1.5 mt-1 sm:ml-[7.75rem]" data-testid={`unit-history-files-${t.id}`}>
                {t.files.map((f, i) => (
                  <li key={i}>
                    <button type="button" onClick={() => download(f)} className="inline-flex items-start max-w-full text-left gap-1 px-2 py-0.5 rounded border border-slate-200 bg-white text-[11px] text-sky-700 hover:underline [overflow-wrap:anywhere]" title={`${f.uploaded_by} · ${f.uploaded_at}`} data-testid={`asset-history-file-${t.id}-${f.id}`}>
                      <Paperclip size={10} className="shrink-0 mt-1" /> {f.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
